/**
 * Agent 状态枚举
 */
export enum EAgentStatus {
  PUBLISHED = 'Published',
  UNPUBLISHED = 'Unpublished',
}

/**
 * Agent 分类枚举（与后端 category 字段值一一对应）
 */
export enum EAgentCategory {
  /** 全部（仅前端 Tabs 使用） */
  ALL = 'all',
  /** 数据Agent */
  DATA_AGENT = 'dataAgent',
  /** 通用对话 */
  GENERAL_CHAT = 'generalChat',
}

/**
 * 分类映射配置项
 */
export interface IAgentCategoryConfig {
  /** 中文名称 */
  labelZh: string;
  /** 英文名称 */
  labelEn: string;
  /** 注释文案（中文） */
  noteZh?: string;
  /** 注释文案（英文） */
  noteEn?: string;
  /** 是否支持折叠（默认 true） */
  collapsible?: boolean;
  /** 是否默认收起（仅 collapsible 为 true 时生效） */
  defaultCollapsed?: boolean;
}

/**
 * Agent 分类映射表（前端硬编码所有中英文文案）
 */
export const AGENT_CATEGORY_MAP: Record<string, IAgentCategoryConfig> = {
  [EAgentCategory.ALL]: {
    labelZh: '全部',
    labelEn: 'All',
  },
  [EAgentCategory.DATA_AGENT]: {
    labelZh: '数据Agent',
    labelEn: 'Data Agent',
    collapsible: false,
  },
  [EAgentCategory.GENERAL_CHAT]: {
    labelZh: '通用对话',
    labelEn: 'General Chat',
    noteZh: '以下模型不查询DataBrain数据',
    noteEn: 'The following models do not query DataBrain data.',
    defaultCollapsed: true,
  },
};

/**
 * 思考模式枚举（单选）
 */
export enum EThinkingMode {
  SMART = 'auto',      // 智能思考
  FAST = 'fastqa',        // 快速思考
  DEEP = 'deepthink',        // 深度思考
}

/**
 * 推荐问题
 */
export interface IRecommendQuestion {
  /** 分类名称（中文） */
  categoryNameCn: string;
  /** 分类名称（英文） */
  categoryNameEn: string;
  /** 排序 */
  sort: number;
  /** 默认图标（非高亮态） */
  defaultIcon?: string;
  /** 激活图标（高亮态） */
  activeIcon?: string;
  /** 激活图标（高亮态 - 暗色主题） */
  activeIconDark?: string;
  /** 数据列表 */
  data: {
    /** 问题内容（中文） */
    questionCn: string;
    /** 问题内容（英文） */
    questionEn: string;
  }[];
}

/**
 * Agent 输入框 @ / # 唤起能力
 */
export enum EAgentMentionCapability {
  /** / 唤起 Skill */
  SKILL = 'skill',
  /** # 唤起 Chart */
  CHART = 'chart',
  /** @ 唤起知识库 */
  KNOWLEDGE = 'knowledge',
}

/**
 * Agent 权限来源（七彩石配置字段 permissionSource）
 * @description 缺省 / 未配置时按 rainbow 处理
 */
export enum EAgentPermissionSource {
  /** 经分业务线 Level 1（userMessage 对应 gameCode） */
  DASHBOARD = 'dashboard',
  /** mgmt 系统全量权限接口 */
  MGMT = 'mgmt',
  /** 七彩石 whitelistGroups（默认） */
  RAINBOW = 'rainbow',
}

/**
 * 经分 userMessage 游戏项（主版本 / 子版本共用字段）
 * @description 对应 POST /api/v1/dashboard/userMessage 的 main_game 元素
 */
export interface IDashboardGamePermissionItem {
  /** 游戏码，与 Agent.gameCode 匹配 */
  game_code: string;
  /** 角色类型；Agent 判定要求为 game */
  role_type?: string;
  /** 角色 id 列表；含 game_level_1 即 Level 1 */
  role_ids?: string[];
  /** 单值角色 id；为 game_level_1 亦算 Level 1 */
  role_id?: string;
  /** 子版本列表 */
  game_List?: IDashboardGamePermissionItem[];
}

/**
 * mgmt 全量权限列表单项
 * @description 对应 POST /api/v1/mgmt_pc/user_permission 的 permissions 元素
 */
export interface IMgmtPermissionItem {
  /** 权限唯一标识，Agent 权限为 agent */
  permission_key: string;
  /** 展示名称 */
  display_name?: string;
  /** 读权限：1 表示可读 */
  readable: number;
  /** 写权限：1 表示可写 */
  writeable?: number;
  /** 数据范围（Agent 判定不使用） */
  data_scope?: unknown;
}

/**
 * 计算 Agent 权限时的外部上下文
 */
export interface IAgentPermissionContext {
  /** 当前用户 openid（rainbow 白名单比对用） */
  userOpenId?: string;
  /**
   * mgmt 全量 permissions 列表
   * @description 未返回或失败时传空数组，mgmt 来源 Agent 视为无权限
   */
  mgmtPermissions?: IMgmtPermissionItem[];
  /**
   * 经分 main_game 列表
   * @description 未返回或失败时传空数组，dashboard 来源 Agent 视为无权限
   */
  dashboardGames?: IDashboardGamePermissionItem[];
}

/**
 * Agent 数据结构
 */
export interface IAgentData {
  /** Agent唯一ID，用于与算法侧关联 */
  agentId: string;
  /** Agent图标URL */
  icon: string;
  /** Agent名称（中文） */
  name_cn: string;
  /** Agent名称（英文） */
  name: string;
  /** Agent描述（中文） */
  description_cn: string;
  /** Agent描述（英文） */
  description: string;
  /** Agent精简描述（中文，用于下拉选项） */
  shortDescription_cn: string;
  /** Agent精简描述（英文，用于下拉选项） */
  shortDescription: string;
  /** Agent作者 */
  author: string;
  /** 作者头像URL */
  authorAvatar: string;
  /** Agent状态 */
  status: EAgentStatus;
  /** 白名单用户组（空数组表示所有用户可见；仅 permissionSource=rainbow 时生效） */
  whitelistGroups: string[];
  /**
   * 权限来源
   * @description dashboard | mgmt | rainbow；缺省按 rainbow
   */
  permissionSource?: EAgentPermissionSource | string;
  /**
   * 经分游戏码
   * @description 仅 permissionSource=dashboard 时使用，对应 userMessage.main_game[].game_code
   */
  gameCode?: string;
  /** 关联的思考模式（单选） */
  thinkingMode: EThinkingMode[];
  /** 分类推荐问题列表 */
  recommendQuestions: IRecommendQuestion[];
  /** 是否默认选中agent集 */
  isDefaultSelected?: boolean;
  /** 是否推荐 */
  isRecommended?: boolean;
  /** 为 true 时在 Agent 集卡片上展示 Demo 标签；未配置或 false 不展示。*/
  isDemo?: boolean;
  /** 是否禁用文件上传 */
  disableFileUpload?: boolean;
  /** 是否隐藏文件上传（默认 false，为 true 时隐藏）*/
  hideFileUpload?: boolean;
  /** 是否默认隐藏思考模式选择器（默认 false，为 true 时隐藏） */
  hideThinkingMode?: boolean;
  /** 当前用户是否有权限访问（按 permissionSource 分流计算） */
  hasPermission?: boolean;
  /** 排序 */
  sort?: number;
  /** 自定义触发器按钮样式类名（用于思考模式按钮样式区分） */
  className?: string;
  /** 选中态图标（用于思考模式按钮） */
  activeIcon?: string;
  /** 是否在触发按钮上显示图标（默认 true） */
  showIcon?: boolean;
  /** 下拉箭头图标（默认态） */
  arrowIcon?: string;
  /** 下拉箭头图标（hover/激活态） */
  arrowIconActive?: string;
  /** 可见场景标识列表（对应 URL 的 template/tab 参数值，如 'dsReport'、'aiExcel'，用于按页签过滤 Agent 显示） */
  visibleScenes?: string[];
  /** 分类标识（与 EAgentCategory 枚举值对应，如 'dataAgent'、'generalChat'） */
  category?: string;
  /**
   * 支持的输入框唤起能力列表
   * 示例: ['skill', 'chart']；未配置或空数组表示均不支持
   */
  mentionCapabilities?: EAgentMentionCapability[];
}
